import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Hidden,
  SwipeableDrawer,
  Drawer,
  Menu,
  MenuItem,
  Chip
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import MenuIcon from '@material-ui/icons/Menu';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import SidebarContent from '../Sidebar/SidebarContent';
import { useAuth } from '../../context/AuthContext';
import { API_ROOT } from '../../config/api';

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
  },
  appBar: {
    // Mobile first - full width by default
    width: '100%',
    marginLeft: 0,
    backgroundColor: '#ffffff',
    color: '#333',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    // Desktop - adjust for sidebar
    [theme.breakpoints.up('md')]: {
      width: `calc(100% - ${drawerWidth}px)`,
      marginLeft: drawerWidth,
    },
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(0, 1),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(0, 2),
    },
  },
  menuButton: {
    marginRight: theme.spacing(1),
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(2),
    marginTop: 56, // Mobile AppBar height
    width: '100%',
    minWidth: 0, // Allow content to shrink
    // Desktop adjustments
    [theme.breakpoints.up('sm')]: {
      marginTop: 64, // Desktop AppBar height
      padding: theme.spacing(3),
    },
    [theme.breakpoints.up('md')]: {
      marginLeft: 0, // Drawer pushes content, no margin needed
    },
  },
  pageTitle: {
    fontWeight: 600,
    color: '#333',
    fontSize: '1rem',
    [theme.breakpoints.up('sm')]: {
      fontSize: '1.25rem',
    },
  },
  serverInfo: {
    fontSize: 10,
    color: '#888',
    marginRight: theme.spacing(1),
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'block',
      fontSize: 12,
      marginRight: theme.spacing(2),
    },
  },
  // Drawer styles
  drawer: {
    [theme.breakpoints.up('md')]: {
      width: drawerWidth,
      flexShrink: 0,
    },
  },
  drawerPaper: {
    width: drawerWidth,
    backgroundColor: '#1a1a2e',
    color: '#ffffff',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
}));

function Layout({ children, title, onRefresh }) {
  const classes = useStyles();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { user, logout, isAdmin } = useAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDrawerClose = () => {
    setMobileOpen(false);
  };

  const handleUserMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
    window.location.href = '/login';
  };

  return (
    <Box className={classes.root}>
      {/* Mobile Drawer - Swipeable for better UX */}
      <Hidden mdUp implementation="css">
        <SwipeableDrawer
          variant="temporary"
          anchor="left"
          open={mobileOpen}
          onClose={handleDrawerClose}
          onOpen={handleDrawerToggle}
          classes={{
            paper: classes.drawerPaper,
          }}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
        >
          <SidebarContent onItemClick={handleDrawerClose} />
        </SwipeableDrawer>
      </Hidden>

      {/* Desktop Drawer - Permanent */}
      <Hidden smDown implementation="css">
        <Drawer
          className={classes.drawer}
          variant="permanent"
          classes={{
            paper: classes.drawerPaper,
          }}
        >
          <SidebarContent />
        </Drawer>
      </Hidden>

      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar className={classes.toolbar}>
          <Box className={classes.titleContainer}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              className={classes.menuButton}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" className={classes.pageTitle} noWrap>
              {title}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center">
            <Typography className={classes.serverInfo}>
              API: {API_ROOT}
            </Typography>
            {onRefresh && (
              <IconButton onClick={onRefresh} size="small">
                <RefreshIcon />
              </IconButton>
            )}
            {user && (
              <>
                <Hidden xsDown>
                  <Chip
                    size="small"
                    label={isAdmin() ? 'Admin' : 'User'}
                    color={isAdmin() ? 'secondary' : 'default'}
                    style={{ marginRight: 8 }}
                  />
                </Hidden>
                <IconButton
                  onClick={handleUserMenuOpen}
                  size="small"
                  style={{ marginLeft: 4 }}
                >
                  <AccountCircleIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleUserMenuClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem disabled>
                    <Typography variant="body2">
                      {user.name} ({user.role})
                    </Typography>
                  </MenuItem>
                  <MenuItem onClick={handleLogout}>Logout</MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      <main className={classes.content}>
        {children}
      </main>
    </Box>
  );
}

export default Layout;
